import Section from 'pxc-editor-core/entities/Section';
import {
  getAllLayers,
  updateCreatedSectionSmart,
  createSectionSmart,
} from 'pxc-editor-core/helpers/schema';
import { showSnack } from './snackbar';
import store from '@/store';
import { SECTION_SUFFIX } from '@/constants';
import {
  getFirstSelectedSection,
  sortCellsByXY,
  createSection,
  createNewPage,
  getShieldBySelectedSection,
  removeMatrixFromShield,
} from '@/helpers';
import {
  checkSyncSlaveShieldBySection,
  overridePages,
  overridePageAction,
  overridePagesAction,
} from './page';
import { createSnapshotThunk } from './undoRedo';
import {
  switchSelectedSection,
  addSection,
  setWizardData,
  copyWizardData,
  handleSectionClick,
  clearSectionsSelection,
} from './main';
import { editSection } from './section';

const SECTION_SELECTION = '@section_selection';
const MATRIX_SELECTION = '@matrix_selection';

const setCopiedSectionsAction = payload => {
  return {
    type: 'COPY&PASTE/SET_COPIED_SECTIONS',
    payload,
  };
};

export const copySectionContent = () => (dispatch, getState) => {
  const {
    core: { sectionsSelection, pages, pagesSelection, selWizard },
  } = getState();
  const selectedSection = getFirstSelectedSection(sectionsSelection, pages);

  if (selectedSection) {
    const { shield } = getAllLayers(selectedSection.shieldId, pages);

    if (shield.isMatrix) {
      const selection =
        Object.keys(sectionsSelection).length > 1
          ? MATRIX_SELECTION
          : SECTION_SELECTION;

      switch (selection) {
        case SECTION_SELECTION: {
          dispatch(
            setCopiedSectionsAction({
              copiedShield: {
                shield: { ...shield, isMatrix: false },
                sections: null,
              },
              copiedSection: {
                ...Section.fromDict(selectedSection)
                  .clone()
                  .toDict(),
              },
            })
          );
          dispatch(
            showSnack({
              intlId: 'app.editor.snackbar.copiedSuccessfully',
              message: 'Copied successfully',
              variant: 'success',
              show: true,
            })
          );

          break;
        }
        case MATRIX_SELECTION: {
          const currentPage = pages[pagesSelection[0]];
          const currentShield = shield;
          const sectionsSelectionIds = Object.keys(sectionsSelection);
          const copiedSections = Object.entries(currentPage.sections).reduce(
            (acc, [key, value]) => {
              if (sectionsSelectionIds.includes(key)) {
                return { ...acc, [key]: value };
              } else if (value.shieldId === currentShield.id) {
                const emptySection = Section.fromDict(value)
                  .clone()
                  .clearContent();
                return { ...acc, [key]: emptySection };
              }
              return acc;
            },
            {}
          );
          const sections = sortCellsByXY(copiedSections);

          dispatch(
            setCopiedSectionsAction({
              copiedShield: { shield: currentShield, sections },
              copiedSection: null,
            })
          );
          dispatch(
            showSnack({
              intlId: 'app.editor.snackbar.copiedSuccessfully',
              message: 'Copied successfully',
              variant: 'success',
              show: true,
            })
          );

          break;
        }
      }
    } else {
      const selectedSections = Object.values(sectionsSelection);

      if (selectedSections.length > 1) {
        dispatch(setCopiedSectionsAction({ copiedSections: selectedSections }));
      } else {
        dispatch(
          setCopiedSectionsAction({
            copiedShield: { shield, sections: null },
            copiedSection: Section.fromDict(selectedSection)
              .clone()
              .toDict(),
          })
        );
      }

      dispatch(
        showSnack({
          intlId: 'app.editor.snackbar.copiedSuccessfully',
          message: 'Copied successfully',
          variant: 'success',
          show: true,
        })
      );
    }
  }

  if (selWizard?.application_type === 'CABLE_FLAG') {
    dispatch(copyWizardData(JSON.stringify(selWizard)));
  }
};

export const pasteSectionContent = ({ withoutFormatting }) => (
  dispatch,
  getState
) => {
  dispatch(createSnapshotThunk());

  const {
    core: {
      sectionsSelection,
      pages,
      pagesSelection,
      pagesOrder,
      shieldsSelection,
      selWizard,
      copiedSelWizard,
      copiedSection,
      copiedShield,
      copiedSections,
    },
  } = getState();
  const selectedSections = Object.values(sectionsSelection);
  const selectedSection = getFirstSelectedSection(sectionsSelection, pages);
  const [currentPageUid] = pagesSelection;
  const currentPage = pages[currentPageUid];

  if (copiedSections.length > 1) {
    dispatch(pasteCopiedSectionsThunk());
  } else {
    if (copiedShield?.shield?.isMatrix) {
      const shields = Object.keys(shieldsSelection).length
        ? Object.keys(shieldsSelection)
        : [selectedSection.shieldId];
      const { shield: selectedShield } = getAllLayers(shields[0], pages);

      if (selectedShield) {
        const currentPage = pages[pagesSelection[0]];
        const { layouts } = currentPage;

        // Remove the selected shield sections
        const sections = Object.entries(currentPage.sections).reduce(
          (acc, [key, value]) => {
            if (value.shieldId !== selectedShield.id) {
              return { ...acc, [key]: value };
            }
            return acc;
          },
          {}
        );

        // Update the shield, convert it to the matrix
        for (let layoutId in layouts) {
          const { groups } = layouts[layoutId];
          for (let groupId in groups) {
            const { shields } = groups[groupId];
            for (let shId in shields) {
              const shield = shields[shId];
              if (shield.id === selectedShield.id) {
                shield.isMatrix = copiedShield.shield.isMatrix;
                shield.isMatrixFixedCellWidth =
                  copiedShield.shield.isMatrixFixedCellWidth;
                shield.isMatrixBordersEnabled =
                  copiedShield.shield.isMatrixBordersEnabled;
                break;
              }
            }
          }
        }

        pages[currentPage.uid].layouts = layouts;
        pages[currentPage.uid].sections = sections;

        dispatch(clearSectionsSelection());
        dispatch(overridePages(pages, pagesOrder, false));

        queueMicrotask(() => {
          const copiedSections = Object.values(
            JSON.parse(JSON.stringify(copiedShield.sections))
          );

          // Determine the original bounding box for the cells
          const minX = Math.min(...copiedSections.map(cell => cell.x));
          const minY = Math.min(...copiedSections.map(cell => cell.y));
          const maxX = Math.max(
            ...copiedSections.map(cell => cell.x + cell.width)
          );
          const maxY = Math.max(
            ...copiedSections.map(cell => cell.y + cell.height)
          );

          // Calculate the scaling factors
          const scaleX = selectedShield.primitiveProps.w / (maxX - minX);
          const scaleY = selectedShield.primitiveProps.h / (maxY - minY);

          // Adjust the cell coordinates to fit within the shield
          const updatedSections = copiedSections.reduce(
            (acc, section, index) => {
              const newX =
                selectedShield.primitiveProps.x + (section.x - minX) * scaleX;
              const newY =
                selectedShield.primitiveProps.y + (section.y - minY) * scaleY;
              const newWidth = section.width * scaleX;
              const newHeight = section.height * scaleY;
              const updatedSection = withoutFormatting
                ? updateCreatedSection(new Section(section))
                : section;

              return [
                ...acc,
                {
                  ...updatedSection,
                  id: `${selectedShield.id}${SECTION_SUFFIX}${index}`,
                  shieldId: selectedShield.id,
                  x: newX,
                  y: newY,
                  width: newWidth,
                  height: newHeight,
                },
              ];
            },
            []
          );

          updatedSections.forEach(section => dispatch(editSection(section)));
        }, 0);
      }
    } else if (copiedSection && selectedSection) {
      const selectedShield = getAllLayers(selectedSection.shieldId, pages)
        .shield;

      if (selectedShield.isMatrix) {
        const sections = Object.values(sectionsSelection).reduce((acc, sec) => {
          if (sec.shieldId === selectedShield.id) {
            const { section } = getAllLayers(sec.id, pages);
            const sectionWithoutFormatting = {
              ...updateCreatedSection(new Section(section)),
              ...Section.getContent(copiedSection),
            };
            const sectionWithFormatting = {
              ...copiedSection,
              ...Section.getBaseSettings(section),
            };
            const sectionToModify = withoutFormatting
              ? sectionWithoutFormatting
              : sectionWithFormatting;

            return { ...acc, [sectionToModify.id]: sectionToModify };
          }
          return acc;
        }, {});

        dispatch(
          overridePageAction({
            page: {
              ...currentPage,
              sections: { ...currentPage.sections, ...sections },
            },
            sectionsSelection: sections,
          })
        );
      } else if (selectedSections.length > 1) {
        const sectionsSelectionIds = Object.keys(sectionsSelection);
        const notSelected = Object.values(currentPage.sections).reduce(
          (acc, item) => {
            if (!sectionsSelectionIds.includes(item.id)) {
              return { ...acc, [item.id]: item };
            }
            return acc;
          },
          {}
        );
        if (withoutFormatting) {
          const updatedSections = Object.values(currentPage.sections).reduce(
            (acc, item) => {
              if (sectionsSelectionIds.includes(item.id)) {
                return {
                  ...acc,
                  [item.id]: {
                    ...updateCreatedSection(new Section(item)),
                    ...Section.getContent(copiedSection),
                  },
                };
              }
              return acc;
            },
            {}
          );

          dispatch(
            overridePageAction({
              page: {
                ...currentPage,
                sections: { ...notSelected, ...updatedSections },
              },
              sectionsSelection: updatedSections,
            })
          );
        } else {
          const newSections = Object.values(shieldsSelection).reduce(
            (acc, item) => {
              const newSection = createSection({
                targetShield: item,
                sourceShield: copiedShield.shield,
                sourceSection: copiedSection,
              });
              const section = { ...newSection, isChanged: true };
              return { ...acc, [section.id]: section };
            },
            {}
          );

          dispatch(
            overridePageAction({
              page: {
                ...currentPage,
                sections: { ...notSelected, ...newSections },
              },
              sectionsSelection: newSections,
            })
          );
        }
      } else {
        const isSelectedSectionEmpty = Section.contentIsEmpty(selectedSection);
        const isSelectedSectionEqualsCopied =
          selectedSection.id === copiedSection.id;
        const isSourceIdSelectedSectionEqualsCopied =
          selectedSection.sourceSectionId === copiedSection.id;
        const isSelectedAndCopiedHaveSameSize =
          selectedSection.width === copiedSection.width &&
          selectedSection.height === copiedSection.height;
        const isEqual =
          isSelectedSectionEqualsCopied ||
          isSourceIdSelectedSectionEqualsCopied ||
          isSelectedAndCopiedHaveSameSize;

        if (isSelectedSectionEmpty) {
          const sectionWithoutFormatting = {
            ...updateCreatedSection(new Section(selectedSection)),
            ...Section.getContent(copiedSection),
          };
          const sectionWithFormatting = {
            ...copiedSection,
            ...Section.getBaseSettings(selectedSection),
          };
          const section = withoutFormatting
            ? sectionWithoutFormatting
            : sectionWithFormatting;

          dispatch(editSection(section));
          dispatch(switchSelectedSection(section));
          dispatch(handleSectionClick({ section }));
        } else if (isEqual) {
          const shiftCoef = 0.5;
          const update = {
            id: `${selectedSection.shieldId}${SECTION_SUFFIX}${Section.counter}`,
            sourceSectionId: copiedSection.id,
            shieldId: selectedSection.shieldId,
            x: selectedSection.x + shiftCoef,
            y: selectedSection.y + shiftCoef,
            width: copiedSection.width,
            height: copiedSection.height,
            isChanged: true,
          };
          const sectionWithoutFormatting = {
            ...updateCreatedSection(new Section(selectedSection)),
            ...Section.getContent(copiedSection),
            ...update,
          };
          const sectionWithFormatting = { ...copiedSection, ...update };
          const section = withoutFormatting
            ? sectionWithoutFormatting
            : sectionWithFormatting;

          dispatch(addSection(section));
          dispatch(switchSelectedSection(section));
          dispatch(handleSectionClick({ section }));
        } else {
          if (withoutFormatting) {
            const section = {
              ...updateCreatedSection(new Section(selectedSection)),
              ...Section.getContent(copiedSection),
            };
            dispatch(editSection(section));
            dispatch(switchSelectedSection(section));
            dispatch(handleSectionClick({ section }));
          } else {
            const section = createSection({
              targetShield: selectedShield,
              sourceShield: copiedShield.shield,
              sourceSection: copiedSection,
            });

            dispatch(addSection(section));
            dispatch(switchSelectedSection(section));
            dispatch(handleSectionClick({ section }));
          }
        }
      }
    }
  }

  if (selWizard?.application_type === 'CABLE_FLAG') {
    dispatch(setWizardData(JSON.parse(copiedSelWizard)));
  }

  setTimeout(() => {
    dispatch(checkSyncSlaveShieldBySection(selectedSection));
  }, 10);

  dispatch(setCopiedSectionsAction({ copiedSections: [] }));
};

export const pasteCopiedSectionsThunk = () => (dispatch, getState) => {
  const {
    core: {
      pages,
      pagesOrder,
      pagesSelection,
      sectionsSelection,
      ribbonColor,
      copiedSections,
    },
  } = getState();
  const [currentPageUid] = pagesSelection;
  const currentPageIndex = pagesOrder.findIndex(uid => uid === currentPageUid);
  const currentPage = pages[currentPageUid];
  const copiedSectionsNumber = copiedSections.length;
  const selectedSections = Object.values(sectionsSelection);
  const sectionsToCopy = [...copiedSections];
  const updatedPages = {};
  const tempPagesOrder = [currentPage.uid];
  const firstSelectedSection = getFirstSelectedSection(
    sectionsSelection,
    pages
  );
  const layers = getAllLayers(firstSelectedSection.shieldId, pages);
  const groupId = layers.group.id;

  let pageIndex = currentPageIndex;
  let updatedSectionsSelection = {};
  let updatedShieldsSelection = {};

  while (!!sectionsToCopy.length) {
    const nextPageUid = pagesOrder[pageIndex];
    const isCurrentPage = currentPageUid === nextPageUid;

    if (isCurrentPage) {
      if (selectedSections.length > 1) {
        const shields = Object.values(selectedSections).reduce(
          (acc, section) => {
            const shield = getShieldBySelectedSection({
              pages: { [currentPage.uid]: currentPage },
              selectedSection: section,
            });
            if (shield) {
              return { ...acc, [shield.id]: shield };
            }
            return acc;
          },
          {}
        );
        const modifiedSections = Object.values(shields).reduce(
          (acc, shield) => {
            const [copiedSection] = sectionsToCopy.splice(0, 1);
            if (copiedSection) {
              const section = createSectionSmart({
                sourceShield: shield,
                pages: { [currentPage.uid]: currentPage },
                ribbonColor,
              });
              if (section) {
                const base = Section.getBaseSettings(section);
                return { ...acc, [section.id]: { ...copiedSection, ...base } };
              }
            }
            return acc;
          },
          {}
        );
        const modifiedShieldIds = Object.values(modifiedSections).map(
          section => section.shieldId
        );
        const notSelectedSections = Object.values(currentPage.sections).reduce(
          (acc, section) => {
            if (!modifiedShieldIds.includes(section.shieldId)) {
              return { ...acc, [section.id]: section };
            }
            return acc;
          },
          {}
        );

        updatedPages[currentPage.uid] = {
          ...removeMatrixFromShield({
            page: currentPage,
            shields: Object.keys(shields),
          }),
          sections: { ...modifiedSections, ...notSelectedSections },
        };
        updatedSectionsSelection = modifiedSections;
        updatedShieldsSelection = Object.values(modifiedSections).reduce(
          (acc, section) => {
            const shield = getShieldBySelectedSection({
              pages: { [currentPage.uid]: currentPage },
              selectedSection: section,
            });
            if (shield) {
              return { ...acc, [shield.id]: shield };
            }
            return acc;
          },
          {}
        );

        break;
      } else if (selectedSections.length === 1) {
        const [selectedSection] = selectedSections;
        const layers = getAllLayers(selectedSection.shieldId, pages);

        if (layers.group) {
          const shields = layers.group.shields;
          const selectedShield = layers.shield;
          const entries = Object.entries(shields);
          const index = entries.findIndex(
            ([key, value]) => value.id === selectedShield?.id
          );
          const nextEntries = entries.slice(
            index,
            index + copiedSectionsNumber
          );
          const nextShields = nextEntries.reduce(
            (acc, [key, value]) => ({ ...acc, [key]: value }),
            {}
          );
          const nextShieldsIds = Object.keys(nextShields);
          const notSelectedSections = Object.values(
            currentPage.sections
          ).reduce((acc, section) => {
            if (!nextShieldsIds.includes(section.shieldId)) {
              return { ...acc, [section.id]: section };
            }
            return acc;
          }, {});
          const nextSections = Object.values(nextShields).reduce(
            (acc, shield) => {
              const section = createSectionSmart({
                sourceShield: shield,
                pages: { [currentPage.uid]: currentPage },
                ribbonColor,
              });
              const [copiedSection] = sectionsToCopy.splice(0, 1);
              if (section && copiedSection) {
                const base = Section.getBaseSettings(section);
                return { ...acc, [section.id]: { ...copiedSection, ...base } };
              }
              return acc;
            },
            {}
          );

          updatedPages[currentPage.uid] = {
            ...removeMatrixFromShield({
              page: currentPage,
              shields: nextShieldsIds,
            }),
            sections: { ...notSelectedSections, ...nextSections },
          };
          updatedSectionsSelection = nextSections;
          updatedShieldsSelection = nextShields;
        }
      }
    } else {
      const isNextPageAvailable = !!nextPageUid;
      const nextPage = isNextPageAvailable
        ? pages[nextPageUid]
        : createNewPage();
      const nextPageGroupLayouts = Object.values(nextPage.layouts);
      const [firstLayout] = nextPageGroupLayouts;
      const nextPageSections = Object.values(firstLayout.groups).reduce(
        (acc, group) => {
          if (!!sectionsToCopy.length) {
            if (groupId === group.id) {
              const sections = Object.values(group.shields).reduce(
                (prevSections, nextShield) => {
                  if (!!sectionsToCopy.length) {
                    const section = createSectionSmart({
                      sourceShield: nextShield,
                      pages: { [nextPage.uid]: nextPage },
                      ribbonColor,
                    });
                    const [copiedSection] = sectionsToCopy.splice(0, 1);
                    if (section && copiedSection) {
                      const base = Section.getBaseSettings(section);
                      return {
                        ...prevSections,
                        [section.id]: { ...copiedSection, ...base },
                      };
                    }
                  }
                  return prevSections;
                },
                []
              );
              return { ...acc, ...sections };
            }
          }
          return acc;
        },
        {}
      );
      const nextPageShieldIds = Object.values(nextPageSections).map(
        item => item.shieldId
      );
      const notSelectedSections = Object.values(nextPage.sections).reduce(
        (acc, item) => {
          if (!nextPageShieldIds.includes(item.shieldId)) {
            return { ...acc, [item.id]: item };
          }
          return acc;
        },
        {}
      );

      updatedPages[nextPage.uid] = {
        ...removeMatrixFromShield({
          page: nextPage,
          shields: nextPageShieldIds,
        }),
        sections: { ...notSelectedSections, ...nextPageSections },
      };
      if (!sectionsToCopy.length) {
        updatedSectionsSelection = nextPageSections;
        updatedShieldsSelection = Object.values(nextPageSections).reduce(
          (acc, section) => {
            const shield = getShieldBySelectedSection({
              pages: { [nextPage.uid]: nextPage },
              selectedSection: section,
            });
            if (shield) {
              return { ...acc, [shield.id]: shield };
            }
            return acc;
          },
          {}
        );
      }

      tempPagesOrder.push(nextPage.uid);
    }

    pageIndex++;
  }

  const [sharedPageUid] = tempPagesOrder;
  const [lastPageUid = currentPageUid] = [...tempPagesOrder].reverse();
  const updatedPagesOrder = pagesOrder.reduce((acc, pageUid) => {
    if (acc.includes(pageUid)) {
      return acc;
    } else if (pageUid === sharedPageUid) {
      return [...acc, ...tempPagesOrder];
    }
    return [...acc, pageUid];
  }, []);

  dispatch(
    overridePagesAction({
      pages: { ...pages, ...updatedPages },
      pagesOrder: updatedPagesOrder,
      sectionsSelection: updatedSectionsSelection,
      shieldsSelection: updatedShieldsSelection,
      paginationPageNum:
        updatedPagesOrder.findIndex(uid => uid === lastPageUid) + 1,
      pagesSelection: [lastPageUid],
    })
  );
};

export default {
  setCopiedSectionsAction,
};

const updateCreatedSection = (section) => {
  const { core: { ribbonColor, paperColor, pages } } = store.getState();
  const { group } = getAllLayers(section.id, pages);
  return updateCreatedSectionSmart({ section, group, ribbonColor, paperColor });
};